import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import API_URL, { EQUIPMENT_DRAWING_UPLOAD_ENDPOINT } from "../Config";
import axiosInstance from "../axiosinstance.js";
import "./jeconDialog.css";
import "./EquipmentDrawingUploadModal.css";

// Allowed drawing types (frontend guard; backend enforces its own allowlist too).
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "tif", "tiff"];
const ALLOWED_ACCEPT =
  ".pdf,.jpg,.jpeg,.png,.tif,.tiff,application/pdf,image/jpeg,image/png,image/tiff";
// Matches the backend MAX_FILES_PER_REQUEST for the equipment-drawing uploader.
const MAX_FILES = 30;

const getExtension = (name = "") =>
  name.includes(".") ? name.split(".").pop().toLowerCase() : "";

const fileKey = (f) => `${f.name}__${f.size}`;

/**
 * Roles permitted to upload equipment drawings from the UI.
 * The backend (`admin_or_planning_field_tech_required`) is the real gate;
 * this only decides whether the upload controls are shown.
 * Read-only / viewer roles fall through to `false` and see view-only UI.
 */
export const canManageEquipmentDrawings = () => {
  const role = (localStorage.getItem("role_name") || "").trim().toLowerCase();
  return ["admin", "administrator", "field technician"].includes(role);
};

const getEquipmentNo = (equipment) =>
  equipment?.equipment_tag_number || equipment?.equipment_no || equipment?.id || "";

const EquipmentDrawingUploadModal = ({
  isOpen,
  onClose,
  equipment,
  onUploaded,
}) => {
  const [files, setFiles] = useState([]);
  const [validationError, setValidationError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null); // {uploaded, skipped[]}
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const equipmentNo = getEquipmentNo(equipment);
  const hasExistingDrawing =
    Array.isArray(equipment?.files) && equipment.files.length > 0;

  // Reset transient state whenever the modal opens for a (new) equipment.
  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setValidationError("");
      setUploadError("");
      setIsUploading(false);
      setUploadProgress(0);
      setUploadResult(null);
      setIsDragging(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [isOpen, equipment?.id]);

  // Accept one or more files: filter by allowed type, dedupe, and enforce the cap.
  const addFiles = (picked) => {
    setUploadError("");
    const incoming = Array.from(picked || []);
    if (incoming.length === 0) return;

    const rejected = [];
    const accepted = incoming.filter((f) => {
      const ok = ALLOWED_EXTENSIONS.includes(getExtension(f.name));
      if (!ok) rejected.push(f.name);
      return ok;
    });

    setFiles((prev) => {
      const seen = new Set(prev.map(fileKey));
      const merged = [...prev];
      for (const f of accepted) {
        if (!seen.has(fileKey(f))) {
          seen.add(fileKey(f));
          merged.push(f);
        }
      }
      if (merged.length > MAX_FILES) {
        setValidationError(`You can upload at most ${MAX_FILES} files at once.`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });

    if (rejected.length > 0) {
      setValidationError(
        "Only PDF, JPG, JPEG, PNG, TIF and TIFF files are allowed.",
      );
    } else if (accepted.length > 0) {
      setValidationError("");
    }
  };

  const removeFile = (key) => {
    setFiles((prev) => prev.filter((f) => fileKey(f) !== key));
  };

  const handleFileChange = (event) => {
    addFiles(event.target.files);
    // Allow re-selecting the same file(s) after a removal.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (isUploading) return;
    addFiles(event.dataTransfer.files);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setValidationError("Please select at least one drawing file to upload.");
      return;
    }
    if (!equipment?.id) {
      setUploadError("Missing equipment reference. Please reopen and retry.");
      return;
    }

    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    formData.append("equipment_id", equipment.id);

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError("");
    try {
      const res = await axiosInstance.post(
        `${API_URL}${EQUIPMENT_DRAWING_UPLOAD_ENDPOINT}`,
        formData,
        {
          onUploadProgress: (evt) => {
            if (evt.total) {
              setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
            }
          },
        },
      );

      if (res?.data?.success === false) {
        throw new Error(
          typeof res.data.message === "string"
            ? res.data.message
            : "Upload failed.",
        );
      }

      const data = res?.data?.data || {};
      const uploaded = data.uploaded_files || [];
      const skipped = data.skipped_files || [];

      // Refresh the parent regardless (counts/list) before deciding what to show.
      onUploaded?.(data);

      if (skipped.length > 0) {
        // Keep the modal open to show what went through and what didn't.
        setUploadResult({ uploaded: uploaded.length, skipped });
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        onClose?.();
      }
    } catch (error) {
      const status = error?.response?.status;
      const apiMessage = error?.response?.data?.message;
      if (status === 403) {
        setUploadError("You do not have permission to upload drawings.");
      } else {
        setUploadError(
          typeof apiMessage === "string"
            ? apiMessage
            : "Failed to upload drawing. Please try again.",
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (isUploading) return; // don't allow closing mid-upload
    onClose?.();
  };

  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        className: "equipment-drawing-upload-paper jecon-dialog",
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 700,
          fontSize: "1.05rem",
          pr: 1,
        }}
      >
        <span>Upload Equipment Drawing</span>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={isUploading}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <p className="equipment-drawing-upload-equipno">
          Equipment No:&nbsp;
          <strong>{equipmentNo || "N/A"}</strong>
        </p>
        {hasExistingDrawing && !uploadResult && (
          <p className="equipment-drawing-upload-hint">
            This equipment already has {equipment.files.length} drawing
            {equipment.files.length > 1 ? "s" : ""}. Uploading will add to them.
          </p>
        )}

        {uploadResult ? (
          <div className="equipment-drawing-upload-summary">
            {uploadResult.uploaded > 0 && (
              <p className="equipment-drawing-upload-summary-ok">
                ✓ {uploadResult.uploaded} drawing
                {uploadResult.uploaded === 1 ? "" : "s"} uploaded successfully.
              </p>
            )}
            <p className="equipment-drawing-upload-summary-head">
              {uploadResult.skipped.length} file
              {uploadResult.skipped.length === 1 ? "" : "s"} skipped:
            </p>
            <ul className="equipment-drawing-upload-skipped">
              {uploadResult.skipped.map((s, i) => (
                <li key={i}>
                  <strong>{s.file || "File"}</strong> — {s.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
        <button
          type="button"
          className={`equipment-drawing-dropzone${
            isDragging ? " is-dragging" : ""
          }`}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!isUploading) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          disabled={isUploading}
        >
          <CloudUploadIcon />
          <span className="equipment-drawing-dropzone-text">
            Click to browse or drag files here
          </span>
          <span className="equipment-drawing-dropzone-sub">
            PDF, JPG, JPEG, PNG, TIF, TIFF — up to {MAX_FILES} files
          </span>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_ACCEPT}
          multiple
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {files.length > 0 && (
          <ul className="equipment-drawing-file-list">
            {files.map((f) => (
              <li key={fileKey(f)} className="equipment-drawing-file-row">
                <FileIcon fontSize="small" />
                <span className="equipment-drawing-file-name" title={f.name}>
                  {f.name}
                </span>
                <IconButton
                  size="small"
                  aria-label={`Remove ${f.name}`}
                  onClick={() => removeFile(fileKey(f))}
                  disabled={isUploading}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </li>
            ))}
          </ul>
        )}

        {validationError && (
          <p className="equipment-drawing-upload-error">{validationError}</p>
        )}
        {uploadError && (
          <p className="equipment-drawing-upload-error">{uploadError}</p>
        )}

        {isUploading && (
          <div className="equipment-drawing-upload-progress">
            <div className="equipment-drawing-upload-progress-head">
              <span>Uploading…</span>
              <span>{uploadProgress}%</span>
            </div>
            <LinearProgress
              variant={
                uploadProgress >= 100 ? "indeterminate" : "determinate"
              }
              value={uploadProgress}
              sx={{
                height: 6,
                borderRadius: 3,
                "& .MuiLinearProgress-bar": { backgroundColor: "#b71c1c" },
                backgroundColor: "#f1d9d9",
              }}
            />
          </div>
        )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        {uploadResult ? (
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              backgroundColor: "#b71c1c",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#9f1919" },
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isUploading}
              sx={{
                borderColor: "#d3a3a3",
                color: "#7f1d1d",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#b91c1c",
                  backgroundColor: "#fff5f5",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={isUploading || files.length === 0}
              startIcon={
                isUploading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudUploadIcon fontSize="small" />
                )
              }
              sx={{
                backgroundColor: "#b71c1c",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#9f1919" },
              }}
            >
              {isUploading
                ? "Uploading…"
                : files.length > 1
                  ? `Upload ${files.length} files`
                  : "Upload"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EquipmentDrawingUploadModal;
